import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { API_BASE, assistantHistoryApi } from "../api";
import { WhatsAppIcon, waUrl } from "./WhatsAppFab";

const SESSION_KEY = "venus_assistant_session";
const WELCOME = {
  id: "welcome",
  role: "assistant",
  content: "Hello — I’m the Venus studio assistant. Ask me about lamps, materials, delivery, gifting, or commissioning a custom piece.",
};

const suggestions = [
  "Which lamp suits a bedside table?",
  "What is PLA material?",
  "Do you make custom pieces?",
];

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const getSessionId = () => {
  try {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved && /^[A-Za-z0-9][A-Za-z0-9-]{7,79}$/.test(saved)) return saved;
    const id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return newId();
  }
};

const renderMessageText = (text) => {
  const parts = [];
  const linkRe = /\[([^\]]+)\]\((\/[^)\s]+)\)/g;
  let last = 0;
  let match;
  while ((match = linkRe.exec(text || "")) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <Link key={`${match.index}-${match[2]}`} to={match[2]} className="text-[var(--copper)] underline underline-offset-4 hover:text-[var(--copper-dark)]">
        {match[1]}
      </Link>
    );
    last = match.index + match[0].length;
  }
  if (last < (text || "").length) parts.push(text.slice(last));
  return parts;
};

const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || historyLoaded) return;
    setLoadingHistory(true);
    assistantHistoryApi(sessionId)
      .then((res) => {
        const history = (res.messages || []).map((m, i) => ({ ...m, id: `${m.created_at || i}-${i}` }));
        if (history.length) setMessages(history);
      })
      .catch(() => toast.error("Could not load your assistant conversation."))
      .finally(() => {
        setLoadingHistory(false);
        setHistoryLoaded(true);
      });
  }, [open, historyLoaded, sessionId]);

  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const appendDelta = (assistantId, delta) => {
    setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + delta } : m)));
  };

  const finishMessage = (assistantId) => {
    setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)));
  };

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const assistantId = newId();
    setMessages((prev) => [
      ...prev,
      { id: newId(), role: "user", content: text },
      { id: assistantId, role: "assistant", content: "", streaming: true },
    ]);

    try {
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Assistant request failed");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const raw of events) {
          const line = raw.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) appendDelta(assistantId, payload.delta);
          if (payload.error) throw new Error(payload.error);
          if (payload.done) finishMessage(assistantId);
        }
        if (done) break;
      }
      finishMessage(assistantId);
    } catch (err) {
      const message = err?.message || "The studio assistant is briefly unavailable.";
      setMessages((prev) => prev.map((m) => (
        m.id === assistantId
          ? { ...m, streaming: false, error: true, content: "I’m briefly unavailable. Please try again, or continue with the studio on WhatsApp." }
          : m
      )));
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const hasConversation = messages.some((m) => m.role === "user");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close Venus AI assistant" : "Open Venus AI assistant"}
        data-testid="assistant-open-button"
        className="fixed bottom-24 right-6 z-40 flex items-center gap-2 bg-[var(--espresso)] text-[var(--cream)] px-4 h-14 rounded-full border border-[var(--copper)]/50 shadow-[0_16px_38px_-14px_rgba(20,12,8,0.65)] hover:bg-[var(--copper)] hover:text-white transition-colors"
      >
        <Sparkles className="w-5 h-5 text-[var(--copper-light)]" />
        <span className="hidden sm:inline text-xs tracking-[0.18em] uppercase font-medium">Ask Venus</span>
      </button>

      {open && (
        <section
          data-testid="assistant-panel"
          aria-label="Venus AI shopping assistant"
          className="fixed z-50 inset-x-4 bottom-40 sm:left-auto sm:right-6 sm:w-[410px] h-[min(620px,calc(100vh-13rem))] bg-[var(--cream)] border border-[var(--sand)] shadow-[0_28px_80px_-28px_rgba(20,12,8,0.55)] flex flex-col overflow-hidden"
        >
          <header className="bg-[var(--espresso)] text-[var(--cream)] px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[var(--copper-light)]">Studio assistant</p>
              <h2 className="font-serif-display text-2xl mt-1">Ask Venus</h2>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close assistant" data-testid="assistant-close-button" className="p-2 text-[var(--cream)]/60 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </header>

          <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto px-4 py-5 space-y-4" aria-live="polite">
            {loadingHistory && (
              <div className="flex items-center gap-2 text-xs text-[var(--espresso)]/50" data-testid="assistant-history-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading conversation…
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`assistant-message-${m.role}`}
                  className={`max-w-[86%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[var(--espresso)] text-[var(--cream)]"
                      : m.error
                        ? "bg-[#fff4ed] border border-[#d8a48f] text-[var(--espresso)]"
                        : "bg-[var(--ivory-soft)] border border-[var(--sand)] text-[var(--espresso)]"
                  }`}
                >
                  {m.content ? renderMessageText(m.content) : <Loader2 className="w-4 h-4 animate-spin text-[var(--copper)]" />}
                  {m.streaming && m.content && <span className="inline-block w-1.5 h-4 ml-1 align-middle bg-[var(--copper)] animate-pulse" />}
                </div>
              </div>
            ))}
            {!hasConversation && (
              <div className="pt-2 flex flex-wrap gap-2" data-testid="assistant-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    disabled={sending}
                    data-testid={`assistant-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="px-3 py-2 text-xs border border-[var(--sand)] text-[var(--espresso)]/70 hover:border-[var(--copper)] hover:text-[var(--copper)] transition-colors disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <footer className="border-t border-[var(--sand)] p-4 bg-[var(--cream)]">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={2}
                maxLength={2000}
                placeholder="Ask about a piece, delivery, materials…"
                aria-label="Message Venus assistant"
                data-testid="assistant-input"
                className="flex-1 resize-none bg-white border border-[var(--sand)] px-3 py-3 text-sm focus:outline-none focus:border-[var(--copper)]"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                aria-label="Send assistant message"
                data-testid="assistant-send-button"
                className="w-12 h-12 bg-[var(--copper)] text-white flex items-center justify-center hover:bg-[var(--copper-dark)] transition-colors disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-[var(--espresso)]/50">
              <span>Powered by Claude · Store facts only</span>
              <a href={waUrl("Hi Venus! I need help with")} target="_blank" rel="noopener noreferrer" data-testid="assistant-whatsapp-link" className="inline-flex items-center gap-1.5 text-[#128C7E] hover:text-[#0d6e60]">
                <WhatsAppIcon className="w-3.5 h-3.5" /> Human help
              </a>
            </div>
          </footer>
        </section>
      )}
    </>
  );
};

export default AssistantWidget;
